import React, { useEffect, useState } from 'react';
import { CommonPageStep, Plans, subscriptionPlanName } from '..';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { Button } from '@mui/material';
import apiHelper, {
  SupportedIncomingFormat,
  SupportedMethod,
  SupportedOutgoingFormat,
  SupportedServers,
} from '../../common/ApiHelper';
import { queryString } from '../../common/CommonTools';
import { useAppSelector } from '../../store/hooks';
import {
  ChildProfile,
  selectAppAuthentication,
  setAppPageStep,
} from '../../store/reducer/appStateSlice';
import { outsideComponentDispatch } from '../../store/store';
import { ThankYouElement } from '../thankYou/ThankYouElement';

export const PaymentProcessing = () => {
  const authenticationData = useAppSelector(selectAppAuthentication);
  const [paymentStatus, setPaymentStatus] =
    useState<{
      payment_status: 'SUCCESS' | 'PENDING' | 'CANCELED';
      create_time: string;
      payment_detail: {
        payment_to: Plans;
        childLevel: ChildProfile['child_profile_data']['childLevel'];
        payment_gateway: string;
        currency: string;
        amount: number;
        payment_method: string;
      };
      payment_gateway: {
        message?: string;
      };
    }>();
  const parmsValue = queryString();
  const [retries, setRetries] = useState(0);

  const thankYouElement = ThankYouElement();

  useEffect(() => {
    void checkPaymentStatus();
  }, [retries]);

  const checkPaymentStatus = async () => {
    const result = await getPaymentStatus(parmsValue.id);
    console.log(result);
    if (
      result.payment_status === 'SUCCESS' ||
      result.payment_status === 'CANCELED'
    ) {
      setPaymentStatus(result);
    } else if (result.payment_status === 'PENDING') {
      setTimeout(() => {
        if (retries >= 0) {
          setPaymentStatus(result);
        } else {
          setRetries((prev) => {
            return prev + 1;
          });
        }
      }, 3000);
    }
  };

  const getPaymentStatus = async (id: string) => {
    return apiHelper(
      SupportedServers.ACCOUNT,
      `/v2/payment/details/${id}`,
      SupportedMethod.GET,
      undefined,
      {
        incomingFormat: SupportedIncomingFormat.JSON,
        headerConstructor() {
          return {
            authorization: authenticationData ? authenticationData.token : '',
          };
        },
      },
    ).then((res) => {
      return res.data.data;
    });
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '100px',
        gap: '16px',
        fontFamily: 'Poppins',
        fontSize: '16px',
        height: '100%',
      }}
    >
      <img
        style={{ width: '200px' }}
        id="desktop-header"
        alt=""
        src="assets/common/bba-logo.svg"
      />
      <span style={{ fontSize: '30px' }}>Payment Status</span>
      {!paymentStatus && <p>Checking payment status...</p>}

      {paymentStatus?.payment_status === 'SUCCESS' && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 5,
            alignItems: 'flex-start',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 5,
              color: 'green',
              fontSize: 20,
            }}
          >
            <CheckCircleOutlineIcon />
            <span>Payment Success</span>
          </div>
          <span>
            Plan:{' '}
            {subscriptionPlanName(paymentStatus.payment_detail.payment_to)}
          </span>
          <span>
            Duration:{' '}
            {thankYouElement.getSubscriptionPlanSubLabel(
              paymentStatus.payment_detail.payment_to,
            )}
          </span>
          <span style={{ textTransform: 'capitalize' }}>
            Level: {paymentStatus.payment_detail.childLevel}
          </span>
          <span>
            Amount: {paymentStatus.payment_detail.currency}{' '}
            {paymentStatus.payment_detail.amount}
          </span>
          <span>
            Payment Gateway: {paymentStatus.payment_detail.payment_gateway}
          </span>
          <span>
            Payment Date: {new Date(paymentStatus.create_time).toDateString()}
          </span>
          {paymentStatus.payment_gateway?.message && (
            <span>Error: {paymentStatus.payment_gateway.message}</span>
          )}
        </div>
      )}
      {paymentStatus?.payment_status === 'PENDING' && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 5,
            alignItems: 'flex-start',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 5,
              color: 'orange',
              fontSize: 20,
            }}
          >
            <WarningAmberRoundedIcon />
            <span>Payment Pending</span>
          </div>
          <span>
            Plan:{' '}
            {subscriptionPlanName(paymentStatus.payment_detail.payment_to)}
          </span>
          <span>
            Duration:{' '}
            {thankYouElement.getSubscriptionPlanSubLabel(
              paymentStatus.payment_detail.payment_to,
            )}
          </span>
          <span style={{ textTransform: 'capitalize' }}>
            Level: {paymentStatus.payment_detail.childLevel}
          </span>
          <span>
            Payment Date: {new Date(paymentStatus.create_time).toDateString()}
          </span>
          {paymentStatus.payment_gateway?.message && (
            <span>Error: {paymentStatus.payment_gateway.message}</span>
          )}
        </div>
      )}
      {paymentStatus?.payment_status === 'CANCELED' && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 5,
            alignItems: 'flex-start',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 5,
              color: 'red',
              fontSize: 20,
            }}
          >
            <CancelOutlinedIcon />
            <span>Payment Canceled</span>
          </div>
          <span>
            Plan:{' '}
            {subscriptionPlanName(paymentStatus.payment_detail.payment_to)}
          </span>
          <span>
            Duration:{' '}
            {thankYouElement.getSubscriptionPlanSubLabel(
              paymentStatus.payment_detail.payment_to,
            )}
          </span>
          <span style={{ textTransform: 'capitalize' }}>
            Level: {paymentStatus.payment_detail.childLevel}
          </span>
          <span>
            Amount: {paymentStatus.payment_detail.currency}{' '}
            {paymentStatus.payment_detail.amount}
          </span>
          <span>
            Payment Gateway: {paymentStatus.payment_detail.payment_gateway}
          </span>
          <span>
            Payment Date: {new Date(paymentStatus.create_time).toDateString()}
          </span>
          {paymentStatus.payment_gateway?.message && (
            <span>Error: {paymentStatus.payment_gateway.message}</span>
          )}
        </div>
      )}

      {/* {paymentStatus && (
        <pre style={{ height: '299px', overflow: 'auto' }}>
          <code>{JSON.stringify(paymentStatus, null, 2)}</code>
        </pre>
      )} */}
      {paymentStatus &&
        (paymentStatus.payment_status === 'SUCCESS' ? (
          <Button
            variant="contained"
            onClick={() => {
              window.location.href = '/BigBangLab';
            }}
          >
            Start BBL
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={() => {
              window.location.href = '/Subscription';
            }}
          >
            Retry Payment
          </Button>
        ))}
    </div>
  );
};
